import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
	ArrowLeft,
	Check,
	Code,
	Copy,
	Download,
	ExternalLink,
	Plus,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { openExternal } from "@/components/app-shell";
import { ConfirmButton } from "@/components/confirm-button";
import { PluckThumbnail } from "@/components/pluck-thumbnail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { track } from "@/lib/analytics";
import { assetUrl } from "@/lib/assets";
import { createPluckNode } from "@/lib/board-graph";
import { queryClient, trpc, trpcClient } from "@/lib/trpc";
import { formatRelativeDate } from "@/lib/utils";

const COPIED_RESET_MS = 1500;

export function PluckDetailPage() {
	const { pluckId } = useParams({ from: "/app/plucks/$pluckId" });
	const navigate = useNavigate();
	const pluck = useQuery(trpc.plucks.get.queryOptions({ id: pluckId }));
	const boards = useQuery(trpc.boards.list.queryOptions());
	const [copied, setCopied] = useState(false);
	const [htmlCopied, setHtmlCopied] = useState(false);

	const remove = useMutation(
		trpc.plucks.delete.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: trpc.plucks.list.queryKey(),
				});
				toast.success("Pluck deleted");
				await navigate({ to: "/" });
			},
		})
	);

	const addToBoard = async (boardId: string | "new") => {
		if (!pluck.data) {
			return;
		}
		let targetId = boardId;
		if (targetId === "new") {
			const created = await trpcClient.boards.create.mutate({
				name: `${pluck.data.sourceDomain} ideas`,
			});
			targetId = created.id;
			track("board.created_from", {
				board_id: created.id,
				from: "pluck_detail",
				existing_boards: boards.data?.length ?? 0,
			});
		}
		const board = await trpcClient.boards.get.query({ id: targetId });
		const nodes = board.state.nodes as { position?: { y: number } }[];
		const lowest = nodes.reduce(
			(max, n) => Math.max(max, n.position?.y ?? 0),
			0
		);
		const node = createPluckNode(pluck.data.id, {
			x: 0,
			y: nodes.length === 0 ? 0 : lowest + 260,
		});
		await trpcClient.boards.saveState.mutate({
			id: targetId,
			state: { ...board.state, nodes: [...board.state.nodes, node] },
			baseRevision: board.revision,
		});
		track("card.added", {
			type: "pluck",
			via: "pluck_detail",
			new_board: boardId === "new",
			board_cards: nodes.length,
		});
		await queryClient.invalidateQueries({
			queryKey: trpc.boards.list.queryKey(),
		});
		await navigate({ to: "/boards/$boardId", params: { boardId: targetId } });
	};

	const copyHtml = async () => {
		if (!pluck.data?.html) {
			return;
		}
		await navigator.clipboard.writeText(pluck.data.html);
		track("pluck.html_copied", { mode: pluck.data.mode });
		setHtmlCopied(true);
		setTimeout(() => setHtmlCopied(false), COPIED_RESET_MS);
	};

	const copyPrompt = async () => {
		if (!pluck.data?.promptText) {
			return;
		}
		await navigator.clipboard.writeText(pluck.data.promptText);
		track("pluck.prompt_copied", { mode: pluck.data.mode });
		setCopied(true);
		setTimeout(() => setCopied(false), COPIED_RESET_MS);
	};

	if (pluck.isPending) {
		return (
			<div className="mx-auto flex max-w-5xl flex-col gap-6 p-8">
				<Skeleton className="h-8 w-40" />
				<Skeleton className="aspect-video w-full rounded-xl" />
			</div>
		);
	}

	if (!pluck.data) {
		return (
			<div className="p-8">
				<p className="text-muted-foreground">This pluck doesn't exist.</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/">Back to library</Link>
				</Button>
			</div>
		);
	}

	const p = pluck.data;

	return (
		<div className="mx-auto flex max-w-5xl flex-col gap-6 p-8">
			<div className="flex items-center gap-3">
				<Button asChild size="icon-sm" variant="ghost">
					<Link aria-label="Back to library" to="/">
						<ArrowLeft />
					</Link>
				</Button>
				<h1 className="min-w-0 flex-1 truncate font-bold font-display text-xl tracking-[-0.02em]">
					{p.sourceTitle || p.sourceDomain}
				</h1>
				<Badge variant="secondary">{p.mode}</Badge>
			</div>

			<div className="grid gap-6 lg:grid-cols-[1fr_320px]">
				<div className="flex items-center justify-center overflow-hidden rounded-xl border bg-[repeating-conic-gradient(var(--color-muted)_0%_25%,transparent_0%_50%)] bg-size-[16px_16px] p-6">
					<PluckThumbnail
						alt={p.sourceTitle ?? p.sourceDomain}
						assetId={p.screenshotAssetId}
						className="max-h-[70vh] max-w-full rounded-sm shadow-sm"
					/>
				</div>

				<aside className="flex flex-col gap-4">
					<div className="flex flex-col gap-2 rounded-xl border bg-card p-4 text-sm">
						<Row label="Source">
							<button
								className="flex items-center gap-1 truncate text-left underline-offset-4 hover:underline"
								onClick={() => {
									track("pluck.source_opened", { mode: p.mode });
									openExternal(p.sourceUrl);
								}}
								type="button"
							>
								<span className="truncate">{p.sourceDomain}</span>
								<ExternalLink className="size-3 shrink-0" />
							</button>
						</Row>
						<Row label="Element">
							<code className="text-xs">&lt;{p.rootTag}&gt;</code>
						</Row>
						{p.selector && (
							<Row label="Selector">
								<code className="break-all text-xs">{p.selector}</code>
							</Row>
						)}
						<Row label="Size">
							{Math.round(p.bounds.width)} × {Math.round(p.bounds.height)} px
							<span className="text-muted-foreground"> @ {p.dpr}x</span>
						</Row>
						<Row label="Viewport">
							{p.viewport.width} × {p.viewport.height}
						</Row>
						<Row label="Plucked">{formatRelativeDate(p.createdAt)}</Row>
					</div>

					<div className="flex flex-col gap-2 rounded-xl border bg-card p-4">
						<p className="font-medium text-sm">Add to board</p>
						<div className="flex flex-col gap-1">
							{boards.data?.map((b) => (
								<Button
									className="justify-start"
									key={b.id}
									onClick={() => addToBoard(b.id)}
									size="sm"
									variant="ghost"
								>
									<Plus />
									<span className="truncate">{b.name}</span>
								</Button>
							))}
							<Button
								className="justify-start"
								onClick={() => addToBoard("new")}
								size="sm"
								variant="outline"
							>
								<Plus />
								New board
							</Button>
						</div>
					</div>

					{(p.html || p.figmaSvgAssetId) && (
						<div className="flex flex-col gap-2 rounded-xl border bg-card p-4">
							<p className="font-medium text-sm">Source</p>
							{p.html && (
								<Button
									className="justify-start"
									onClick={copyHtml}
									size="sm"
									variant="ghost"
								>
									{htmlCopied ? <Check /> : <Code />}
									{htmlCopied ? "Copied" : "Copy HTML"}
								</Button>
							)}
							{p.figmaSvgAssetId && (
								<Button
									className="justify-start"
									onClick={() => {
										if (p.figmaSvgAssetId) {
											track("pluck.figma_svg_opened", { mode: p.mode });
											openExternal(assetUrl(p.figmaSvgAssetId));
										}
									}}
									size="sm"
									variant="ghost"
								>
									<Download />
									Open Figma SVG
								</Button>
							)}
						</div>
					)}

					<ConfirmButton
						confirmLabel="Delete this pluck?"
						onConfirm={() => remove.mutate({ id: p.id })}
						size="sm"
						variant="ghost"
					>
						Delete
					</ConfirmButton>
				</aside>
			</div>

			{p.figmaSvgAssetId && (
				<section className="flex flex-col gap-2">
					<h2 className="font-medium text-sm">Figma vectors</h2>
					<div className="flex items-center justify-center overflow-hidden rounded-xl border bg-card p-6">
						<img
							alt="SVG export of the plucked element"
							className="max-h-[60vh] max-w-full"
							src={assetUrl(p.figmaSvgAssetId)}
						/>
					</div>
				</section>
			)}

			{p.promptText && (
				<section className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<h2 className="font-medium text-sm">LLM prompt</h2>
						<Button onClick={copyPrompt} size="sm" variant="outline">
							{copied ? <Check /> : <Copy />}
							{copied ? "Copied" : "Copy prompt"}
						</Button>
					</div>
					<pre className="max-h-96 overflow-auto rounded-xl border bg-card p-4 text-xs leading-relaxed">
						{p.promptText}
					</pre>
				</section>
			)}
		</div>
	);
}

function Row({
	label,
	children,
}: {
	children: React.ReactNode;
	label: string;
}) {
	return (
		<div className="grid grid-cols-[80px_1fr] items-baseline gap-2">
			<span className="text-muted-foreground text-xs">{label}</span>
			<div className="min-w-0">{children}</div>
		</div>
	);
}
